import React, {useState, useEffect} from 'react';
import {IComment} from '../../model/commentModel';
import CommentHeader from './CommentHeader';
import CommentBody from './CommentBody';
import styled from 'styled-components';
import Constants, {AnnotationModes, ServiceNames} from '../../constants/Constants';
import {TscStyles, TscThemeName, TscThemeNames, Spinner} from '@techsmith/tsc-cloud-style-guide';
import TimeFormatter from '../../util/TimeFormatter';
import {useTranslation} from 'react-i18next';
import CommentInput from './CommentInput';
import conversationApi from '../../service/conversationApi';
import DeleteCommentAlert from './DeleteCommentAlert';
import {scrollCommentIntoView} from '../../util/CommentScroller';
import {SetupWorkerState} from '../../model/workerState';
import config from '../../service/config';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {commentsStore, updateCommentsState} from '../../context/commentsProvider';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import {useTscTheme} from '../../context/themeProvider';
import {quizDataStore} from '../../context/quizDataProvider';
import {mediaStore} from '../../context/mediaProvider';
import logging from '../../service/logging';
import {importMediaInterface} from '../../hooks/dynamicImport';

export const CommentWrapper = styled.div<{theme: TscThemeNames; isReply: boolean; isExpanded: boolean; isSelected: boolean; isHighlighted: boolean}>`
   @keyframes highlight-comment {
      0%, 10%{
         border-color: transparent;
         box-shadow: 'none';
      }
      20%, 70% {
         box-shadow: 0 0 0.1px 2px ${TscStyles.color.action.primary};
      }
      85% {
         border-color: transparent;
      }
      100% {
         border-color: ${TscStyles.color.action.primary};
      }
   }

   padding: ${props => props.isReply ? '0.5rem 0.5rem 0 1.5rem' : '0.5rem'};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
   font-weight: 600;
   font-size: 0.875rem;
   animation: ${props => props.isHighlighted ? 'highlight-comment 3500ms ease-in-out' : 'none'};
   transition: ${props => props.isReply ? 'none' : 'all 0.25s ease'};
   position: relative;
   background-color: ${props => props.isReply ? 'transparent' : props.isSelected ? props.theme === TscThemeName.dusk ? TscStyles.color.text.mediumDark : TscStyles.color.ui.dawn.light : props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.lightMedium};
   border-radius: ${TscStyles.border.radius.lg};
   margin: ${props => props.isReply ? '0.25rem 0.5rem 0.5rem 1rem' : '0.25rem 0.5rem 0.5rem 0.5rem'};
   box-shadow: ${props => props.isReply ? 'none' : props.isSelected ? props.theme === TscThemeName.dusk ? 'rgba(0, 0, 0, 0.16) 0px 0px 1.5px, rgba(0, 0, 0, 0.12) 0px 1px 5px, 0 0 3.5px rgba(255,255,255, 0.1) inset' : 'rgba(0, 0, 0, 0.10) 0px 0px 1.5px, rgba(0, 0, 0, 0.06) 0px 1px 5px' : props.isExpanded ? props.theme === TscThemeName.dusk ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1.5px 0 rgba(0, 0, 0, 0.06)' : 'none'};

   :before {
      content: '';
      position: absolute;
      top: 2rem;
      left: 1.1875rem;
      width: 2px;
      z-index: 0;
      bottom: ${props => props.isExpanded ? '0' : '100%'};
      transition: bottom 0.25s ease-out;
      background: ${props => props.isExpanded && !props.isReply ? props.theme === TscThemeName.dusk ? `linear-gradient(180deg, ${TscStyles.color.text.medium} 60%, transparent 95%)` : `linear-gradient(180deg, ${TscStyles.color.action.secondaryDawn} 60%, transparent 100%)` : 'transparent'};
   }
`;

const CommentAndRepliesWrapper = styled.li<{theme: TscThemeNames; isReply: boolean}>`
   &:last-of-type {
      margin-bottom: ${props => props.isReply ? '0' : '0.5rem'};
   }
   :first-of-type {
   }
`;
const RepliesWrapper = styled.ol<{theme: TscThemeNames; replyCount: number; isExpanded: boolean}>`
   padding: 0 0 1rem 0;
   list-style-type: none;
   position: relative;

  :before {
   content: '';
   position: absolute;
   top: -0.5rem;
   left: 1.6875rem;
   width: 20px;
   bottom: -0.5rem;
   animation: ${props => props.isExpanded ? 'draw-comment-line 0.5s ease-out forwards' : 'none'};
   border: 2px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.medium : TscStyles.color.action.secondaryDawn};
   border-right: none;
   border-top: none;
   opacity: 0;
   clip-path: inset(0 90% 0 0);
   border-radius: 0 0 0 ${TscStyles.border.radius.lg};
}

   @keyframes draw-comment-line {
      0%, 50%{
         bottom: 100%;
         opacity: 0;
      }
      85% {
         bottom: -0.5rem;
         clip-path: inset(0 90% 0 0);
      }
      100% {
         clip-path: inset(0 0 0 0);
         opacity: 1;
      }
   }

   article {overflow-x: clip;}
   article > * {
      opacity: 100%;
      position: relative;
      animation: comment-slide-in 350ms ease-in calc(var(--animation-order) * 100ms) backwards;
      @keyframes comment-slide-in {
         0% {
            opacity: 0;
            transform: translateX(8px);
         }

         100% {
            opacity: 1;
         }
       }

      :after {
         content: '';
         position: absolute;
         top: 15px;
         left: 27px;
         width: 1rem;
         height: 0.5rem;
         border: 2px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.medium : TscStyles.color.action.secondaryDawn};
         border-right: none;
         border-top: none;
         border-radius: 0 0 0 ${TscStyles.border.radius.lg};
         animation: draw-comment-arc 500ms ease-out calc( var(--animation-order) * 100ms) backwards;
         opacity: 1;

         @keyframes draw-comment-arc {
            0%, 55% {
               clip-path: inset(0 100% 0 0);
               transform: traslateX(8px);
               opacity: 0;
            }
            100% {
               clip-path: inset(0 0 0 0);
            }
        }

      }
   }
`;

export const CommentBase: React.FC<ICommentProps & IStateMappedProps> = ({comments, comment, isReply, shouldShowUpdate, setCommentsToHighlight, SelectedComment, VideoReviewId, annotationMode, IsQuizzing, IsVideo}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [deleteAnnotationOnSave, setDeleteAnnotationOnSave] = useState(false);
   const [expandReplies, setExpandReplies] = useState(false);
   const [loadingState, setLoadingState] = SetupWorkerState();
   const [showDeleteAlertModal, setShowDeleteAlertModal] = useState(false);
   const isSelected = SelectedComment && (SelectedComment.id === comment.id || SelectedComment.id === comment.parentCommentId || comment.replies.includes(SelectedComment));
   const commentContainerRef = React.useRef(null);

   useEffect(() => {
      comment.replies.includes(SelectedComment) && setExpandReplies(true);
      SelectedComment?.id === comment.id && scrollCommentIntoView(commentContainerRef.current);
   }, [SelectedComment, comment]);

   useEffect(() => {
      !expandReplies && comment.isFiltered === false && !!comment.replies.filter(r => r.isFiltered === false).length && setExpandReplies(true);
   }, [comment.isFiltered, comment.replies]);

   useEffect(() => {
      shouldShowUpdate && !isReply && scrollCommentIntoView(commentContainerRef.current as HTMLElement);
   }, [shouldShowUpdate]);

   const submitReply = async (replyText: string) => {
      try {
         setLoadingState({isWorking: true, hasError: false});
         const commentsCopy = Object.assign([], comments);
         const newReply = Object.assign({},
            await conversationApi.addReply({
               text: replyText.trim(),
               annotation: '',
               originatingService: config.featureSwitches.IsScreencast ? ServiceNames.screencast : ServiceNames.knowmia
            }, comment),
            {
               user: {
                  ProfileImageUrl: config.user.AvatarImageUrl,
                  DisplayName: config.user.DisplayName,
                  Id: config.user.TechSmithId
               }
            }
         );

         if (config.featureSwitches.IsScreencast && config.user.IsSignedIn) {
            if (await conversationApi.isSubscribedToConversation(VideoReviewId) === null) {
               await conversationApi.subscribeToConversation(VideoReviewId);
            }
         }

         const updatedComment: IComment = Object.assign({}, commentsCopy.find(c => c.id === comment.id), {replies: comment.replies.concat(newReply), isFiltered: false});
         commentsCopy.splice(commentsCopy.indexOf(comment), 1, updatedComment);
         setCommentsToHighlight(commentsCopy, [newReply]);
         updateCommentsState({comments: commentsCopy});
         updateSelectedConversationState({SelectedComment: newReply});
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to submit reply: ', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const deleteComment = async (commentToDelete: IComment) => {
      setLoadingState({isWorking: true, hasError: false});

      try {
         const deletedComment = await conversationApi.deleteComment(commentToDelete);
         const commentsCopy = Object.assign([], comments);

         if (isReply) {
            const parentCommentIndex = commentsCopy.findIndex(c => c.id === deletedComment.parentCommentId);
            const replies = commentsCopy[parentCommentIndex].replies;
            const replyIndex = replies.findIndex((r: IComment) => r.id === deletedComment.id);

            if (parentCommentIndex > -1 && replyIndex > -1) {
               replies.splice(replyIndex, 1);
            } else {
               const errorText = parentCommentIndex > -1 ? 'Reply could not be found' : 'Parent comment could not be found';
               throw new Error(errorText);
            }

            if (replies.length === 0) {
               setExpandReplies(false);

               if (commentsCopy[parentCommentIndex].isDeleted) {
                  commentsCopy.splice(parentCommentIndex, 1);
               }
            }
         } else {
            const commentIndex = commentsCopy.findIndex((oldComment: IComment) => oldComment.id === deletedComment.id);

            if (commentIndex > -1) {
               if (comments[commentIndex].replies.length) {
                  commentsCopy[commentIndex] = Object.assign({}, comments[commentIndex], {text: t('conversations.commentDeletedText'), annotation: '', isDeleted: true, isEditing: false});
               } else {
                  commentsCopy.splice(commentIndex, 1);
               }
               updateAnnotationManagerState({annotationMode: AnnotationModes.none});
            } else {
               throw new Error('Comment could not be found');
            }
         }

         setCommentsToHighlight(commentsCopy, []);
         updateCommentsState({comments: commentsCopy});
         setDeleteAnnotationOnSave(false);
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to delete comment: ', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const saveCommentEdits = async (editedText: string) => {
      const updatedComment = Object.assign({}, comment, {text: editedText.trim(), isEditing: false, annotation: deleteAnnotationOnSave ? '' : comment.annotation});

      if (!updatedComment.text && !updatedComment.annotation) {
         setShowDeleteAlertModal(true);
         return;
      } else if (!updatedComment.text) {
         updatedComment.text = t('conversations.annotationAttached');
      }

      try {
         setLoadingState({isWorking: true, hasError: false});
         updatedComment.text = editedText;
         const editedComment = await conversationApi.editComment(updatedComment);
         const commentsCopy = Object.assign([], comments);

         if (isReply) {
            const parentCommentIndex = commentsCopy.findIndex(c => c.id === updatedComment.parentCommentId);
            const replies = commentsCopy[parentCommentIndex].replies;
            const replyIndex = replies.findIndex((r: IComment) => r.id === updatedComment.id);
            (parentCommentIndex > -1 && replyIndex > -1) && replies.splice(replyIndex, 1, editedComment);
         } else {
            commentsCopy.splice(commentsCopy.findIndex((oldComment: IComment) => oldComment.id === editedComment.id), 1, editedComment);
         }

         updateCommentsState({comments: commentsCopy});
         setLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to edit comment: ', e);
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const selectAndPause = async () => {
      const mediaInterface = await importMediaInterface();
      const timeDifference = TimeFormatter.getRoundedTime(Math.abs(mediaInterface.currentTime - comment.time));
      const timeMismatch = IsVideo && timeDifference > Constants.annotation.timeTolerance;

      if (!isSelected || timeMismatch || (SelectedComment && SelectedComment?.id !== comment.id) || annotationMode === AnnotationModes.edit) {
         const topLevelComment = isReply ? comments.find(c => c.id === comment.parentCommentId) : comment;
         updateSelectedConversationState({SelectedComment: comment});
         updateAnnotationManagerState({annotationMode: topLevelComment.annotation ? AnnotationModes.display : AnnotationModes.none});

         if (!IsQuizzing && timeMismatch) {
            mediaInterface.pause();
            mediaInterface.currentTime = comment.time;
         }
      }
   };

   const commentRepliesLength = comment.replies.filter(r => !r.isFiltered).length;
   const isCommentOwner = comment.authorId === config.user.TechSmithId;

   const wrapperClassName = isSelected ? 'comment-wrapper selected-comment' : 'comment-wrapper';

   return (
      comment.isFiltered ? null : (
         <CommentAndRepliesWrapper isReply={isReply}>
            <CommentWrapper
               className={wrapperClassName}
               data-test-id="comment-wrapper"
               ref={commentContainerRef}
               theme={theme}
               isReply={isReply}
               isExpanded={expandReplies}
               isSelected={isSelected}
               onClick={selectAndPause}
               isHighlighted={!isReply && shouldShowUpdate}
            >
               <CommentHeader
                  isReply={isReply}
                  comment={comment}
                  deleteAnnotationOnSave={deleteAnnotationOnSave}
                  isSelected={isSelected}
                  setDeleteAnnotationOnSave={setDeleteAnnotationOnSave}
                  isCommentOwner={isCommentOwner}
                  onCommentClick={selectAndPause}
               />
               {loadingState.isWorking && <Spinner themeName={TscThemeName.dawn} />}
               {showDeleteAlertModal && (
                  <DeleteCommentAlert
                     setShowDeleteAlert={setShowDeleteAlertModal}
                     onClose={() => setShowDeleteAlertModal(false)}
                     comment={comment}
                     deleteComment={deleteComment}
                  />
               )}
               {!loadingState.isWorking && (
                  <CommentBody
                     setDeleteAnnotationOnSave={setDeleteAnnotationOnSave}
                     deleteAnnotationOnSave={deleteAnnotationOnSave}
                     handleExpandReplies={() => setExpandReplies(!comment.replies.length ? !expandReplies : true)}
                     isCommentOwner={isCommentOwner}
                     comment={comment}
                     isReply={isReply}
                     saveEdit={(editedText: string) => saveCommentEdits(editedText)}
                     loadingState={loadingState}
                     setShowDeleteAlertModal={setShowDeleteAlertModal}
                     setExpandReplies={setExpandReplies}
                     expandReplies={expandReplies}
                     commentRepliesLength={commentRepliesLength}
                  />
               )}
            </CommentWrapper>
            {(expandReplies && !isReply) && (
               <>
                  <RepliesWrapper
                     theme={theme}
                     replyCount={commentRepliesLength}
                     isExpanded={expandReplies}
                  >
                     {!!commentRepliesLength && (
                        comment.replies.map((reply: IComment, index: number) => (
                           <article
                              key={index}
                              style={{'--animation-order': `${index}`} as React.CSSProperties}
                           >
                              <CommentBase
                                 key={index}
                                 isReply={true}
                                 comment={reply}
                                 shouldShowUpdate={shouldShowUpdate}
                                 setCommentsToHighlight={setCommentsToHighlight}
                                 SelectedComment={SelectedComment}
                                 VideoReviewId={VideoReviewId}
                                 comments={comments}
                                 annotationMode={annotationMode}
                                 IsQuizzing={IsQuizzing}
                                 IsVideo={IsVideo}
                              />
                           </article>
                        ))
                     )}
                  </RepliesWrapper>
                  <CommentInput
                     hasAnnotation={false}
                     inputId={`${comment.id}-reply-input`}
                     setDeleteAnnotationOnSave={setDeleteAnnotationOnSave}
                     isCommentOwner={isCommentOwner}
                     isReply={true}
                     onSubmit={submitReply}
                     loadingState={loadingState}
                  />
               </>
            )}
         </CommentAndRepliesWrapper>
      )
   );
};

export interface ICommentProps {
   comment: IComment;
   isReply: boolean;
   shouldShowUpdate: boolean;
   setCommentsToHighlight(prevComments: IComment[], newComments: IComment[]): void;
}

export interface IStateMappedProps {
   SelectedComment: IComment;
   VideoReviewId: string;
   comments: IComment[];
   annotationMode: AnnotationModes;
   IsQuizzing: boolean;
   IsVideo: boolean;
}

const mapStatesToProps = (
   {comment, isReply, shouldShowUpdate, setCommentsToHighlight}: ICommentProps,
   {SelectedComment, VideoReviewId}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {annotationMode}: Partial<IStateMappedProps>,
   {IsQuizzing}: Partial<IStateMappedProps>,
   {IsVideo}: Partial<IStateMappedProps>
): ICommentProps & IStateMappedProps => ({comments, comment, isReply, shouldShowUpdate, setCommentsToHighlight, SelectedComment, VideoReviewId, annotationMode, IsQuizzing, IsVideo});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore, commentsStore, annotationManagerStore, quizDataStore, mediaStore)(CommentBase);
